import React, {useCallback, useEffect, useRef, useState} from 'react';
import {useSelector} from 'react-redux';
import {MapContainer, Marker, TileLayer, useMap, useMapEvents, Popup} from 'react-leaflet';
import L from 'leaflet';
import {Box} from '@mui/material';
import 'leaflet/dist/leaflet.css';
import {LIGHTNING_MEDIUM, LIGHTNING_OLD, LIGHTNING_RECENT, USER_LOCATION} from '../../constants/mapIcons';
import {getTranslation} from '../../i18n';

// Map container style
const containerStyle = {
    width: '100%',
    height: '100%'
};

// Default map options
const defaultOptions = {
    zoomControl: false,
    attributionControl: true,
};

// Default zoom level
const defaultZoom = 11;

// Fix for default marker icons in Leaflet
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
    iconRetinaUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png',
    iconUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png',
    shadowUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png',
});

// Map controller component to handle map events and updates
const MapController = ({onBoundsChange, setMap, center}) => {
    const map = useMap();

    // Set map reference
    useEffect(() => {
        setMap(map);
    }, [map, setMap]);

    // Update map center when center prop changes
    useEffect(() => {
        if (center && map) {
            map.setView([center.lat, center.lng]);
        }
    }, [center, map]);

    // Handle map events
    useMapEvents({
        moveend: () => {
            const bounds = map.getBounds();
            onBoundsChange({
                north: bounds.getNorth(),
                east: bounds.getEast(),
                south: bounds.getSouth(),
                west: bounds.getWest(),
            });
        },
    });

    return null;
};

const MapComponent = ({center, events, onBoundsChange, focusedEventId, onMapReady}) => {
    const {theme, language} = useSelector((state) => state.ui);
    const [map, setMap] = useState(null);
    const [markers, setMarkers] = useState([]);
    const [mapCenter, setMapCenter] = useState(center || {lat: 42.6977, lng: 23.3219}); // Default to Sofia, Bulgaria
    const [focusedEvent, setFocusedEvent] = useState(null);
    const markerRefs = useRef({});
    const translations = getTranslation(language);
    const t = translations.map.controls;

    // Update mapCenter when center prop changes
    useEffect(() => {
        if (center) {
            setMapCenter(center);
        }
    }, [center]);

    // Expose map methods to parent component
    useEffect(() => {
        if (map && onMapReady) {
            onMapReady({
                zoomIn: () => map.zoomIn(),
                zoomOut: () => map.zoomOut(),
                setView: (center, zoom) => map.setView(center, zoom)
            });
        }
    }, [map, onMapReady]);

    // Find focused event when focusedEventId changes
    useEffect(() => {
        if (focusedEventId && events.length > 0) {
            const event = events.find(e => e.id === focusedEventId);
            if (event) {
                setFocusedEvent(event);
                setMapCenter({lat: event.latitude, lng: event.longitude});

                // We'll handle animation in the marker component
            }
        }
    }, [focusedEventId, events]);

    // Create markers from events
    useEffect(() => {
        if (events.length > 0) {
            const newMarkers = events.map(event => {
                // Calculate how old the event is
                const now = new Date();
                const eventTime = new Date(event.timestamp);
                const ageInMinutes = (now - eventTime) / (1000 * 60);

                // Determine marker color based on age
                let iconUrl;
                if (ageInMinutes <= 5) {
                    iconUrl = LIGHTNING_RECENT; // Red
                } else if (ageInMinutes <= 30) {
                    iconUrl = LIGHTNING_MEDIUM; // Orange
                } else {
                    iconUrl = LIGHTNING_OLD; // Yellow
                }

                // Create custom icon
                const customIcon = new L.Icon({
                    iconUrl: iconUrl,
                    iconSize: [24, 24],
                    iconAnchor: [12, 12],
                });

                return {
                    id: event.id,
                    position: [event.latitude, event.longitude],
                    icon: customIcon,
                    title: `Lightning at ${eventTime.toISOString().replace(/\.\d+Z$/, 'Z')}`,
                    event,
                    isFocused: event.id === focusedEventId,
                };
            });

            setMarkers(newMarkers);
        }
    }, [events, focusedEventId]);

    // Reference to marker elements
    const setMarkerRef = (id, element) => {
        if (element) {
            markerRefs.current[id] = element;
        } else {
            delete markerRefs.current[id];
        }
    };

    // Create user location icon
    const userLocationIcon = useCallback(() => {
        return new L.Icon({
            iconUrl: USER_LOCATION,
            iconSize: [25, 41],
            iconAnchor: [12, 41],
            popupAnchor: [1, -34],
            shadowSize: [41, 41]
        });
    }, []);

    return (
        <Box className="map-container" sx={{height: '80vh'}}>
            <MapContainer
                style={containerStyle}
                center={[mapCenter.lat, mapCenter.lng]}
                zoom={defaultZoom}
                {...defaultOptions}
            >
                <TileLayer
                    attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                    url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                />
                <MapController
                    onBoundsChange={onBoundsChange}
                    setMap={setMap}
                    center={mapCenter}
                />
                {/* User location marker */}
                {center && (
                    <Marker
                        position={[center.lat, center.lng]}
                        icon={userLocationIcon()}
                        title={t.yourLocation}
                    />
                )}
                {markers.map(marker => (
                    <Marker
                        key={marker.id}
                        position={marker.position}
                        icon={marker.icon}
                        title={marker.title}
                        ref={(element) => setMarkerRef(marker.id, element)}
                    >
                        <Popup>
                            <div>
                                <strong>{t.lightning}</strong>
                                <div>{new Date(marker.event.timestamp).toISOString().replace(/\.\d+Z$/, 'Z')}</div>
                            </div>
                        </Popup>
                    </Marker>
                ))}
            </MapContainer>
        </Box>
    );
};

export default MapComponent;
